import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { deDE } from '@clerk/localizations';
import ProtectedRoute from './components/ProtectedRoute';
import StageBanner from './components/StageBanner';
import SubscriptionGate from './components/SubscriptionGate';
import DashboardPage from './pages/DashboardPage';
import AssessmentPage from './pages/AssessmentPage';
import PricingPage from './pages/PricingPage';
import OnboardingPage from './pages/OnboardingPage';
import MockCheckoutPage from './pages/MockCheckoutPage';
import AccountPage from './pages/AccountPage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import AuditsPage from './pages/AuditsPage';
import AnonymousCheckPage from './pages/AnonymousCheckPage';
import ControlsPage from './pages/ControlsPage';
import SettingsPage from './pages/SettingsPage';
import { useClerkTokenInterceptor } from './services/api';
import { ActiveAuditProvider, useActiveAudit } from './contexts/ActiveAuditContext';

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const clerkLocalization = {
  ...deDE,
  signIn: {
    ...deDE.signIn,
    start: {
      ...deDE.signIn.start,
      title: 'Anmelden bei Audicio',
      subtitle: 'Willkommen zurück! Bitte melden Sie sich an.',
    },
  },
  signUp: {
    ...deDE.signUp,
    start: {
      ...deDE.signUp.start,
      title: 'Konto bei Audicio erstellen',
      subtitle: 'Erstellen Sie Ihr Konto, um loszulegen.',
    },
  },
};

// „Audit öffnen" (Story 2.3, Routing-Tabelle Spec 1.2): setzt das Audit aus der
// URL als aktives Audit und leitet zur Übersicht weiter.
function OpenAudit() {
  const { auditId } = useParams();
  const { setActiveAuditId } = useActiveAudit();
  const navigate = useNavigate();

  useEffect(() => {
    setActiveAuditId(auditId);
    navigate('/dashboard', { replace: true });
  }, [auditId, setActiveAuditId, navigate]);

  return null;
}

// Erfordert Login UND ein zugangsberechtigtes Abo (trialing/active). Ohne Abo
// leitet SubscriptionGate auf /pricing. Nur noch für die kostenpflichtigen
// Paid-Tier-Seiten (Maßnahmen/Controls) — der Free-Tier (Audit, Score, Report,
// Dashboard) läuft ohne Abo hinter ProtectedRoute (AUD-56).
function Gated({ children }) {
  return (
    <ProtectedRoute>
      <SubscriptionGate>{children}</SubscriptionGate>
    </ProtectedRoute>
  );
}

function AppRoutes() {
  useClerkTokenInterceptor();

  return (
    <Routes>
      <Route path="/login/*" element={<LoginPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />

      {/* Anonymer CyberRisikoCheck (AUD-53): kein Login, kein Abo — startet direkt
          den DIN-SPEC-27076-Fragebogen, identifiziert über httpOnly-Cookie. */}
      <Route path="/check" element={<AnonymousCheckPage />} />

      {/* Nur Login nötig, kein Abo-Gate — sonst könnte man ohne Abo nie zur
          Pricing-Seite/zum Checkout gelangen bzw. sein Konto nicht einsehen. */}
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PricingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* Lokale Mock-Bezahlseite (nur relevant bei PAYMENT_PROVIDER=mock). */}
      <Route
        path="/mock-checkout"
        element={
          <ProtectedRoute>
            <MockCheckoutPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/*"
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        }
      />

      {/* Free-Tier (AUD-56): Login genügt — Audit, Score, Report, Dashboard sind
          ohne aktives Abo nutzbar. Serverseitig sind nur die Paid-Deltas gated. */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/assessment/:assessmentId" element={<ProtectedRoute><AssessmentPage /></ProtectedRoute>} />
      <Route path="/audits" element={<ProtectedRoute><AuditsPage /></ProtectedRoute>} />
      <Route path="/audits/:auditId" element={<ProtectedRoute><OpenAudit /></ProtectedRoute>} />
      {/* Paid-Tier: Maßnahmen-Detailansicht + Fortschritt erfordern ein aktives Abo. */}
      <Route path="/audits/:auditId/controls" element={<Gated><ControlsPage /></Gated>} />
      <Route path="/audits/:auditId/controls/:controlId" element={<Gated><ControlsPage /></Gated>} />
      <Route path="/settings" element={<Navigate to="/settings/stammdaten" replace />} />
      <Route path="/settings/:section" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ClerkProvider publishableKey={clerkPubKey} localization={clerkLocalization}>
      <StageBanner />
      <Router>
        <ActiveAuditProvider>
          <AppRoutes />
        </ActiveAuditProvider>
      </Router>
    </ClerkProvider>
  );
}

export default App;
